import React, { useState, useMemo, useCallback } from 'react';
import {
  Typography,
  Button,
  Box,
  Paper,
  useTheme,
  useMediaQuery,
  Fade,
  Grow,
  Chip,
  Stack,
  Grid,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import AppLayout from '../components/layout/AppLayout';
import { ContactCard, ContactsTable, ContactStats } from '../components/contacts';
import SearchBar from '../components/common/SearchBar';
import ContactFormModal from '../components/forms/ContactFormModal';
import { useContacts, useSearchAndFilter } from '../hooks';
import usePageOperations from '../hooks/usePageOperations';
import { filterContacts } from '../utils/contactUtils';
import { THEME_COLORS, BREAKPOINTS } from '../constants';

/**
 * Página de gestión de contactos
 */
const ContactsPageComponent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(BREAKPOINTS.MOBILE));

  const contactsHook = useContacts();
  const { contacts, stats, loading, error, pagination, goToPage, changePageSize } = contactsHook;

  const {
    openCreateModal,
    openEditModal,
    openDeleteModal,
    FormModal,
    DeleteModal,
    OperationModal,
  } = usePageOperations({
    entityName: 'Contacto',
    entityNamePlural: 'Contactos',
    crudFunctions: {
      create: contactsHook.createContact,
      update: contactsHook.updateContact,
      delete: contactsHook.deleteContact,
    },
    FormModalComponent: ContactFormModal,
    entityIdKey: 'contact_id',
  });

  const {
    searchInput,
    setSearchInput,
    searchTerm,
    filterStatus,
    handleSearch,
    handleClearSearch,
    handleKeyPress,
    handleFilterChange,
  } = useSearchAndFilter();
  
  const [expandedContactId, setExpandedContactId] = useState(null);

  const filteredContacts = useMemo(() => {
    let filtered = filterContacts(contacts, searchTerm);
    if (filterStatus === 'active') {
      filtered = filtered.filter(contact => contact.hasActiveSubscription);
    } else if (filterStatus === 'inactive') {
      filtered = filtered.filter(contact => !contact.hasActiveSubscription);
    }
    return filtered;
  }, [contacts, searchTerm, filterStatus]);

  const handleExpand = useCallback((contactId) => {
    setExpandedContactId(prev => (prev === contactId ? null : contactId));
  }, []);

  if (error) {
    return (
      <AppLayout currentPage="contacts">
        <Box sx={{ paddingY: 4 }}>
          <Typography variant="h6" color="error" align="center">
            Error al cargar contactos: {error}
          </Typography>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout currentPage="contacts">
      <Fade in timeout={500}>
        <Box>
          <Box sx={{ marginBottom: 4 }}>
            <Typography
              variant="h4"
              sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}
            >
              Gestión de Contactos
            </Typography>
            <Typography variant="body1" sx={{ color: THEME_COLORS.TEXT_MUTED, fontSize: { xs: '0.875rem', sm: '1rem' }, mt: 1 }}>
              Aquí puedes crear, visualizar y administrar todos tus contactos.
            </Typography>
          </Box>

          <Grow in timeout={700}>
            <Box>
              <ContactStats stats={stats} />
            </Box>
          </Grow>

          <Grow in timeout={800}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                marginTop: 4,
                marginBottom: 4,
                border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
                borderRadius: '16px',
                backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
              }}
            >
              <Grid container>
                <Grid item xs={12}>
                  <SearchBar
                    searchInput={searchInput}
                    searchTerm={searchTerm}
                    placeholder="Buscar contactos por nombre, celular o email..."
                    onSearchInputChange={setSearchInput}
                    onSearch={handleSearch}
                    onClear={handleClearSearch}
                    onKeyPress={handleKeyPress}
                  />
                </Grid>
                <Grid item xs={12} sx={{ pt: 0.5 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="flex-start"
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                  >
                    <Chip label="Todos" onClick={() => handleFilterChange('all')} clickable variant={filterStatus === 'all' ? 'filled' : 'outlined'} sx={{ borderRadius: '8px', fontWeight: 500 }} />
                    <Chip label="Con Suscripción Activa" onClick={() => handleFilterChange('active')} clickable variant={filterStatus === 'active' ? 'filled' : 'outlined'} sx={{ borderRadius: '8px', fontWeight: 500 }} />
                    <Chip label="Sin Suscripción Activa" onClick={() => handleFilterChange('inactive')} clickable variant={filterStatus === 'inactive' ? 'filled' : 'outlined'} sx={{ borderRadius: '8px', fontWeight: 500 }} />
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grow>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openCreateModal}
              sx={{
                backgroundColor: THEME_COLORS.TEXT_PRIMARY,
                color: THEME_COLORS.BACKGROUND_PAPER,
                borderRadius: '12px',
                paddingX: 3,
                paddingY: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#1a1a1a' },
              }}
            >
              Nuevo Contacto
            </Button>
          </Box>

          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {filteredContacts.map(contact => (
                <ContactCard
                  key={contact.contact_id}
                  contact={contact}
                  onEdit={() => openEditModal(contact)}
                  onDelete={() => openDeleteModal(contact)}
                  isExpanded={expandedContactId === contact.contact_id}
                  onExpand={handleExpand}
                />
              ))}
            </Box>
          ) : (
            <ContactsTable
              contacts={filteredContacts}
              expandedContactId={expandedContactId}
              onExpand={handleExpand}
              onEdit={openEditModal}
              onDelete={openDeleteModal}
              loading={loading}
              // Props de paginación
              pagination={pagination}
              onPageChange={goToPage}
              onPageSizeChange={changePageSize}
              // Función para refrescar datos después de crear suscripción
              onRefresh={contactsHook.loadContacts}
            />
          )}
        </Box>
      </Fade>
      
      <FormModal />
      <DeleteModal />
      <OperationModal />
    </AppLayout>
  );
};

// Memoizar el componente para optimización
const ContactsPage = React.memo(ContactsPageComponent);
ContactsPage.displayName = 'ContactsPage';

export default ContactsPage; 